import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Paper,
  LinearProgress,
} from "@mui/material";

import { TRADES_GROUP_BY_OPTIONS } from "../utils/consts";
import {
  Grouping,
  AggregatedTradeData,
  RawData,
  Trade,
  TransactionStatusEnum,
} from "../types";
import {
  buildGroupTree,
  capitaliseFirstLetter,
  formatTradeTime,
} from "../utils/utils";
import { renderGroupRowsWithRowSpan } from "./utils";
import { ReportTypes } from "../ReportsDashboard";

interface TradesTableProps {
  groupings: Grouping[];
  isLoading: boolean;
  isFetching: boolean;
  rawData?: RawData<Trade>;
  aggregatedTradeData?: AggregatedTradeData[];
}

const TradesTable = ({
  groupings,
  isLoading,
  isFetching,
  rawData,
  aggregatedTradeData,
}: TradesTableProps) => {
  const aggregatedColumns = [
    "Trading Result (USD)",
    "Number of Trades",
    "Trading Turnover (USD)",
    "Win Count & Rate (%)",
    "Loss Count & Rate (%)",
    "Draw Count & Rate (%)",
    "Revert Count & Rate (%)",
  ];

  const rawColumns = [
    "Trade ID",
    "Client ID",
    "Trade status",
    "Trade direction",
    "Instrument",
    "Bet Amount (USD)",
    "Trading Result (USD)",
    "Risk Flag",
    "Payout Ratio",
    "Created At",
    "Trade Length",
    "User Country",
    "Game ID",
  ];

  const columns = groupings.length > 0 ? aggregatedColumns : rawColumns;

  const cellSx = {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  };
  const rawRows =
    isLoading || isFetching
      ? []
      : rawData?.map((trade, idx) => {
          const tradingResult =
            trade.transactionStatus === TransactionStatusEnum.Won
              ? `-$${Math.abs(
                  ((trade.potentialWinnings || 0) - trade.amount) / 100
                ).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : trade.transactionStatus === TransactionStatusEnum.Lost
              ? `$${(trade.amount / 100).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "-";

          const riskProfile =
            trade.riskFlag === null
              ? ""
              : trade.riskFlag === "0"
              ? "Normal"
              : capitaliseFirstLetter(trade.riskFlag.toLowerCase());

          return (
            <TableRow key={idx}>
              <TableCell align="right" sx={cellSx}>
                {trade.tradeId.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {trade.userId.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {capitaliseFirstLetter(
                  trade.transactionStatus.toLocaleString().toLowerCase()
                )}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {capitaliseFirstLetter(
                  trade.tradeDirection.toLocaleString().toLowerCase()
                )}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {trade.asset.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`$${(trade.amount / 100).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              </TableCell>
              <TableCell
                align="right"
                sx={cellSx}
                style={{
                  color:
                    trade.transactionStatus === TransactionStatusEnum.Lost
                      ? "#4caf50"
                      : trade.transactionStatus === TransactionStatusEnum.Won
                      ? "#f44336"
                      : undefined,
                }}
              >
                {tradingResult}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {riskProfile}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {`${
                  trade.payoutRatio === null
                    ? "-"
                    : trade.payoutRatio.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                }`}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {new Date(trade.createdAt).toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {trade.tradeTime !== null
                  ? formatTradeTime(trade.tradeTime)
                  : "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {trade.country ? trade.country.toLocaleString() : "-"}
              </TableCell>
              <TableCell align="right" sx={cellSx}>
                {trade.gameId}
              </TableCell>
            </TableRow>
          );
        });

  return (
    <TableContainer
      component={Paper}
      sx={{ mt: 2 }}
      style={{
        border: "1px solid rgba(255, 255, 255, 0.1)",
        boxShadow: "none",
      }}
    >
      <MuiTable>
        <TableHead>
          <TableRow>
            {groupings.map((g, idx) => (
              <TableCell
                key={idx}
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textTransform: "none",
                }}
              >
                <b style={{ textTransform: "none" }}>
                  {TRADES_GROUP_BY_OPTIONS.find(
                    (opt) => opt.value === g.groupBy
                  )?.name || g.groupBy}
                </b>
              </TableCell>
            ))}
            {columns.map((col, idx) => (
              <TableCell
                sx={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  textTransform: "none",
                }}
                key={idx}
              >
                <b style={{ textTransform: "none" }}>{col}</b>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading || isFetching || (!aggregatedTradeData && !rawData) ? (
            <TableRow sx={{ padding: 0 }}>
              <TableCell colSpan={groupings.length + 6} sx={{ padding: 0 }}>
                <LinearProgress />
              </TableCell>
            </TableRow>
          ) : null}
          {aggregatedTradeData &&
            groupings.length > 0 &&
            renderGroupRowsWithRowSpan(
              buildGroupTree(
                aggregatedTradeData.filter((row) => {
                  // Split the groupKey by ' + ' to get each grouping's value
                  const keyParts = row.groupKey.split(" + ");
                  return groupings.every((g, idx) => {
                    const filter: string[] = g.groupFilter;
                    if (!filter || filter.length === 0) return true;
                    return filter.includes(keyParts[idx]);
                  });
                }),
                groupings
              ),
              groupings,
              undefined,
              ReportTypes.Trades
            )}
          {rawData && groupings.length === 0 && rawRows}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default TradesTable;
